/**
 * Discount Price Updater
 *
 * This script dynamically updates product prices on the page when specific discount codes
 * are applied to the cart. It works with SKIO subscription pricing to show both subscription
 * and one-time purchase prices with discounts applied.
 *
 * How it works:
 * 1. Reads discount codes and original prices from window.getDopa (set by Liquid -n 'section/discount-code-price-amends.liquid')
 * 2. Fetches the current cart to check which discount codes are active
 * 3. If a matching discount code is found, calculates and displays discounted prices
 * 4. If multiple discount codes match, uses the one with the highest percentage
 *
 * Dependencies:
 * - window.getDopa.discountCodes: Array of discount code configurations
 * - window.getDopa.originalPrices: Object containing subscription and onetime prices
 * - SKIO attributes: [skio-subscription-price], [skio-onetime-price], [skio-external-subscription-price]
 */

document.addEventListener("DOMContentLoaded", function () {
  const discountPriceUpdater = new DiscountPriceUpdater();
  discountPriceUpdater.init();
});

class DiscountPriceUpdater {
  constructor() {
    // Load original prices from window.getDopa object (populated by Liquid template)
    // Falls back to hardcoded values if window.getDopa is not available
    this.originalPrices = window.getDopa?.originalPrices || {
      subscription: 4000, // £40.00 in pence (fallback)
      onetime: 4500, // £45.00 in pence (fallback)
    };

    // Load discount code configurations from window.getDopa
    // Each discount code should have: { name: "CODE", amount: "10" }
    this.discountCodes = window.getDopa?.discountCodes || [];
    this.currency = window.getDopa?.currency || "GBP";
    this.locale =
      window.getDopa?.locale || document.documentElement.lang || "en-GB";
  }

  /**
   * Initialize the price updater
   * Validates that discount codes are available and triggers the price check
   */
  init() {
    // Exit early if no discount codes are configured
    if (!this.discountCodes || this.discountCodes.length === 0) {
      return;
    }

    // Check cart and update prices on page load
    this.checkAndUpdatePrices();
  }

  /**
   * Fetch the current cart from Shopify
   * @returns {Promise<Object|null>} Cart object or null on error
   */
  async fetchCart() {
    try {
      const response = await fetch("/cart.js");

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching cart:", error);
      return null;
    }
  }

  /**
   * Check if any configured discount codes are applied to the cart
   * If multiple codes are found, returns the one with the highest discount percentage
   *
   * @param {Object} cart - Shopify cart object
   * @returns {Object|null} Discount configuration object or null if no match
   */
  isDiscountCodeApplied(cart) {
    // Validate cart has discount codes
    if (!cart || !cart.discount_codes || !Array.isArray(cart.discount_codes)) {
      return null;
    }

    // Collect all matching discount codes
    const matchedDiscounts = [];

    for (const discountConfig of this.discountCodes) {
      // Skip entries with empty discount code names
      if (!discountConfig.name) {
        continue;
      }

      // Check if this discount code is applied to the cart (case-insensitive)
      const appliedCode = cart.discount_codes.find(
        (code) =>
          code.code &&
          code.code.toLowerCase() === discountConfig.name.toLowerCase(),
      );

      if (appliedCode) {
        matchedDiscounts.push(discountConfig);
      }
    }

    // Handle multiple matching discounts by selecting the highest percentage
    if (matchedDiscounts.length > 1) {
      return matchedDiscounts.reduce((best, current) => {
        const currentAmount = parseFloat(current.amount) || 0;
        const bestAmount = parseFloat(best.amount) || 0;
        return currentAmount > bestAmount ? current : best;
      });
    }

    // Return single match or null if none found
    return matchedDiscounts.length === 1 ? matchedDiscounts[0] : null;
  }

  /**
   * Format a price in the currency minor unit to a localized currency string
   * @param {number} priceInPence - Price in smallest currency unit
   * @returns {string} Formatted price string (e.g., "£40.00")
   */
  formatPrice(priceInPence) {
    try {
      const formatter = new Intl.NumberFormat(this.locale, {
        style: "currency",
        currency: this.currency,
      });
      const fractionDigits = formatter.resolvedOptions().maximumFractionDigits;
      const divisor = Math.pow(10, fractionDigits);
      return formatter.format(priceInPence / divisor);
    } catch (error) {
      console.error("Error formatting price:", error);
      return `£${(priceInPence / 100).toFixed(2)}`;
    }
  }

  /**
   * Calculate the discounted price
   * @param {number} originalPrice - Original price in pence
   * @param {number} discountPercent - Discount percentage (e.g., 10 for 10%)
   * @returns {number} Discounted price in pence
   */
  calculateDiscountedPrice(originalPrice, discountPercent) {
    const discountAmount = (originalPrice * discountPercent) / 100;
    return originalPrice - discountAmount;
  }

  /**
   * Update all price elements on the page with discounted prices
   * @param {Object|null} discountConfig - Discount configuration or null to restore original prices
   */
  updatePriceElements(discountConfig) {
    // If no discount is active, restore original prices
    if (!discountConfig) {
      this.restoreOriginalPrices();
      // Dispatch event to notify other scripts that prices were restored
      document.dispatchEvent(new CustomEvent('discount-price-updated', { 
        detail: { active: false } 
      }));
      return;
    }

    // Discount is active - remove skio-save elements before applying discounted prices
    const skioSaveElements = document.querySelectorAll(".skio-save");
    skioSaveElements.forEach((element) => {
      element.remove();
    });

    // Calculate discounted prices for both subscription and one-time purchases
    const subscriptionPrice = this.calculateDiscountedPrice(
      this.originalPrices.subscription,
      discountConfig.amount,
    );
    const onetimePrice = this.calculateDiscountedPrice(
      this.originalPrices.onetime,
      discountConfig.amount,
    );

    // Update all SKIO subscription price elements
    const subscriptionPriceElements = document.querySelectorAll(
      "[skio-subscription-price]",
    );
    subscriptionPriceElements.forEach((element) => {
      this.applyDiscountedPrice(element, subscriptionPrice);
    });

    // Update all SKIO one-time price elements
    const onetimePriceElements = document.querySelectorAll(
      "[skio-onetime-price]",
    );
    onetimePriceElements.forEach((element) => {
      this.applyDiscountedPrice(element, onetimePrice);
    });

    // Update external subscription price elements (if any exist)
    const externalSubscriptionElements = document.querySelectorAll(
      "[skio-external-subscription-price]",
    );
    externalSubscriptionElements.forEach((element) => {
      this.applyDiscountedPrice(element, subscriptionPrice);
    });

    // Dispatch event to notify other scripts that discount prices were applied
    document.dispatchEvent(new CustomEvent('discount-price-updated', { 
      detail: { 
        active: true,
        subscriptionPrice,
        onetimePrice
      } 
    }));
  }

  /**
   * Apply discounted price styling to a single element
   * Shows the discounted price prominently with the original price struck through
   *
   * @param {HTMLElement} element - Price element to update
   * @param {number} discountedPrice - Discounted price in pence
   */
  applyDiscountedPrice(element, discountedPrice) {
    // Skip if this element has already been processed
    if (element.querySelector(".discounted-price")) {
      return;
    }

    // Store the original price text before modification
    const originalContent = element.textContent;

    // Remove SKIO attributes to prevent conflicts with SKIO's own price updates
    this.removeSkioAttributes(element);

    // Create strikethrough element for original price (red, struck through)
    const strikethroughPrice = document.createElement("s");
    strikethroughPrice.style.textDecoration = "line-through";
    strikethroughPrice.style.color = "#FF6333";
    strikethroughPrice.textContent = originalContent;

    // Create discounted price element (styled via CSS)
    const discountedPriceElement = document.createElement("span");
    discountedPriceElement.className = "discounted-price";
    discountedPriceElement.style.fontWeight = "bold";
    discountedPriceElement.style.color = "black";
    discountedPriceElement.textContent = this.formatPrice(discountedPrice);

    // Rebuild element structure: [original price (struck through)] [discounted price]
    element.innerHTML = "";
    element.appendChild(strikethroughPrice);
    element.appendChild(document.createTextNode(" "));
    element.appendChild(discountedPriceElement);
  }

  /**
   * Remove SKIO-specific attributes from an element
   * This prevents SKIO from re-processing elements we've already modified
   *
   * @param {HTMLElement} element - Element to clean
   */
  removeSkioAttributes(element) {
    const skioAttributes = [
      "skio-subscription-price",
      "skio-onetime-price",
      "skio-external-subscription-price",
      "skio-discount",
      "skio-plan-picker",
      "skio-auto-init",
      "skio-discount-format",
      "skio-main-product",
    ];

    skioAttributes.forEach((attr) => {
      if (element.hasAttribute(attr)) {
        element.removeAttribute(attr);
      }
    });
  }

  /**
   * Restore original prices by removing discount modifications
   * Called when no discount codes are active
   */
  restoreOriginalPrices() {
    // Find all price elements (both SKIO attributes and our modified elements)
    const allPriceElements = document.querySelectorAll(
      "[skio-subscription-price], [skio-onetime-price], [skio-external-subscription-price], .discounted-price",
    );

    allPriceElements.forEach((element) => {
      const discountedElement = element.querySelector(".discounted-price");

      // If this element was modified by us, restore original text
      if (discountedElement) {
        const strikethroughElement = element.querySelector("s");
        if (strikethroughElement) {
          element.textContent = strikethroughElement.textContent;
        }
      }
    });
  }

  /**
   * Main workflow: Check cart and update prices accordingly
   * This is the primary entry point called on page load
   */
  async checkAndUpdatePrices() {
    try {
      // Fetch current cart state
      const cart = await this.fetchCart();

      // If cart fetch fails, keep original prices
      if (!cart) {
        this.updatePriceElements(null);
        return;
      }

      // Check if any configured discount codes are active
      const discountConfig = this.isDiscountCodeApplied(cart);

      // Update prices based on discount status
      this.updatePriceElements(discountConfig);
    } catch (error) {
      console.error("Error checking and updating prices:", error);
      // Fallback to original prices on any error
      this.updatePriceElements(null);
    }
  }
}
